import React, { useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Alert
} from '@mui/material'
import {
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  Assignment as TaskIcon,
  Security as SecurityIcon
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { PageHeader } from '../components/ui'

// Import componentele existente
import AdminPacsPage from './AdminPacsPage'
import AdminHisPage from './AdminHisPage'
import TenantsListPage from './TenantsListPage'
import AdminSettingsPage from './AdminSettingsPage'
import TaskManagementTab from '../components/admin/TaskManagementTab'
import RolesPermissionsTab from '../components/admin/RolesPermissionsTab'

// Styling compact pentru sub-tab-uri
const subTabsSx = {
  minHeight: 28,
  '& .MuiTab-root': {
    minHeight: 28,
    py: 0.25,
    px: 1,
    fontSize: '0.72rem',
    textTransform: 'none'
  }
}

// Componenta pentru tab-ul Tenant cu sub-tab-uri  
const TenantManagementTab: React.FC = () => {
  const [tenantSubTab, setTenantSubTab] = useState(0)

  const tenantSubTabs = [
    { label: 'Tenant-uri', component: <TenantsListPage /> },
    { label: 'PACS', component: <AdminPacsPage /> },
    { label: 'HIS', component: <AdminHisPage /> }
  ]

  return (
    <Box>
      {/* Sub-tabs pentru Tenant Management - compact */}
      <Tabs 
        value={tenantSubTab} 
        onChange={(_, newValue) => setTenantSubTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={subTabsSx}
      >
        {tenantSubTabs.map((subTab, index) => (
          <Tab key={index} label={subTab.label} />
        ))}
      </Tabs>
      
      {/* Tab content */}
      <Box sx={{ mt: 1.5 }}>
        {tenantSubTabs[tenantSubTab]?.component}
      </Box>
    </Box>
  )
}

// Componente pentru Config - clean, fără wrapper-e
const ConfigSystemTab: React.FC = () => (
  <AdminSettingsPage initialTab={0} hideHeader={true} />
)

const ConfigAppTab: React.FC = () => (
  <AdminSettingsPage initialTab={1} hideHeader={true} />
)

const AdminPage: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState(0)

  // Verifică permisiunile - admin și operator au acces
  if (!user || !['admin', 'operator'].includes(user.role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nu aveți permisiuni pentru a accesa această pagină.
        </Alert>
      </Box>
    )
  }

  // Definim tab-urile cu iconițe - filtrate după rol
  // Instituții și Utilizatori au fost mutate în pagina Configurare
  const allTabs = [
    {
      label: 'Tenant Management',
      icon: <BusinessIcon />,
      component: <TenantManagementTab />,
      roles: ['admin']
    },
    {
      label: 'Roluri & Permisii',
      icon: <SecurityIcon />,
      component: <RolesPermissionsTab />,
      roles: ['admin']
    },
    {
      label: 'Config Sistem',
      icon: <SettingsIcon />,
      component: <ConfigSystemTab />,
      roles: ['admin']
    },
    {
      label: 'Config Aplicație',
      icon: <TuneIcon />,
      component: <ConfigAppTab />,
      roles: ['admin']
    },
    {
      label: 'Task Management',
      icon: <TaskIcon />,
      component: <TaskManagementTab />,
      roles: ['admin']
    }
  ]

  // Filtrează tab-urile în funcție de rolul utilizatorului
  const tabs = allTabs.filter(tab => tab.roles.includes(user.role))

  // Tab-urile ca element pentru PageHeader - Compact
  const tabsElement = (
    <Tabs 
      value={activeTab} 
      onChange={(_, newValue: number) => setActiveTab(newValue)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        minHeight: 36,
        '& .MuiTab-root': {
          minHeight: 36,
          fontSize: '0.78rem',
          py: 0.5,
          px: 1.5,
          textTransform: 'none',
          '& .MuiSvgIcon-root': { fontSize: 16, mr: 0.5 }
        }
      }}
    >
      {tabs.map((tab, index) => (
        <Tab
          key={index}
          icon={tab.icon}
          iconPosition="start"
          label={tab.label}
        />
      ))}
    </Tabs>
  )

  return (
    <Box>
      {/* Unified Page Header cu titlu și tab-uri */}
      <PageHeader
        title="Administrare Sistem"
        tabs={tabsElement}
      />

      {/* Content pentru tab-ul activ */}
      {tabs[activeTab]?.component}
    </Box>
  )
}

export default AdminPage
